import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageChoice, LanguageMenu, ThemeChoice, ThemeMenu, UserMenu } from '@easydent/shared-ui';
import { useAuthStore } from '@/stores/authStore';

const LANGUAGES = [
  { code: 'ro', label: 'Română' },
  { code: 'en', label: 'English' },
];

/**
 * Header + page frame. Deliberately plain: this is a work queue, not a dashboard.
 *
 * <p>
 * The header is brand + nav + avatar, and nothing else. It previously ran the brand, the nav, a
 * language dropdown, a theme dropdown, the user's name and a sign-out button along one row that
 * could not wrap, which overflowed a phone. Display preferences now live inside the user menu,
 * where they are found by looking for settings rather than by scanning a toolbar.
 * </p>
 *
 * <p>
 * Below `sm` the nav drops to its own scrollable row. A hamburger would be the other option, but
 * with two destinations it would hide them behind a tap for no gain.
 * </p>
 */
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const name = useAuthStore((s) => s.name);
  const logout = useAuthStore((s) => s.logout);

  const navItems = [
    { to: '/', label: t('nav.referrals', 'Referrals') },
    { to: '/billing', label: t('nav.billing', 'Billing') },
    { to: '/account', label: t('nav.account', 'Your data') },
  ];

  const preferences = (
    <>
      <ThemeChoice labels={{
        label: t('theme.label', 'Theme'), light: t('theme.light', 'Light'),
        dark: t('theme.dark', 'Dark'), system: t('theme.system', 'Auto'),
      }} />
      <LanguageChoice
        value={i18n.language}
        languages={LANGUAGES}
        onChange={(code) => i18n.changeLanguage(code)}
        label={t('language', 'Language')}
      />
    </>
  );

  return (
    <div className="min-h-screen bg-canvas">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4">
          <button onClick={() => navigate('/')} className="shrink-0 text-lg font-bold text-brand-ink-strong">
            easyDent <span className="font-normal text-muted">{t('portalName', 'Imaging')}</span>
          </button>

          {name && (
            <nav className="ml-4 hidden gap-1 text-sm sm:flex">
              {navItems.map((n) => (
                <button key={n.to} onClick={() => navigate(n.to)}
                  className="rounded-lg px-2 py-1 text-muted-strong hover:bg-subtle">
                  {n.label}
                </button>
              ))}
            </nav>
          )}

          <div className="ml-auto flex items-center gap-2">
            {name ? (
              <UserMenu
                name={name}
                items={[]}
                preferences={preferences}
                onLogout={() => { logout(); navigate('/login', { replace: true }); }}
                logoutLabel={t('signOut', 'Sign out')}
              />
            ) : (
              // Signed out there is no menu to put them in, so fall back to the compact dropdowns.
              // The inline controls are sized for a menu panel and would be far too wide here.
              <>
                <LanguageMenu value={i18n.language} languages={LANGUAGES}
                  onChange={(code) => i18n.changeLanguage(code)} aria-label={t('language', 'Language')} />
                <ThemeMenu labels={{
                  label: t('theme.label', 'Theme'), light: t('theme.light', 'Light'),
                  dark: t('theme.dark', 'Dark'), system: t('theme.system', 'System'),
                }} />
              </>
            )}
          </div>
        </div>

        {/* Mobile nav: its own row, scrollable rather than wrapped, so the header height is
            predictable however many destinations appear later. */}
        {name && (
          <nav className="flex gap-1 overflow-x-auto border-t border-line-soft px-4 py-2 text-sm sm:hidden">
            {navItems.map((n) => (
              <button key={n.to} onClick={() => navigate(n.to)}
                className="shrink-0 rounded-lg px-3 py-1 text-muted-strong hover:bg-subtle">
                {n.label}
              </button>
            ))}
          </nav>
        )}
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6">{children}</main>
    </div>
  );
};

export default Layout;
